import { useMemo, useState } from "react";
import * as XLSX from "xlsx";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { ArrowsClockwise, UploadSimple, Warning, CheckCircle, XCircle, Info } from "@phosphor-icons/react";

function pick(row, keys) {
  for (const k of Object.keys(row)) {
    if (keys.includes(k.trim().toLowerCase())) return row[k];
  }
  return undefined;
}

function numOrNull(v) {
  const s = String(v ?? "").trim();
  if (s === "") return null;
  const n = Number(s);
  return Number.isNaN(n) ? null : n;
}

function normalizeRows(json) {
  return json
    .map((r) => ({
      sku: String(pick(r, ["sku", "variant sku", "base sku"]) ?? "").trim(),
      count: Number(pick(r, ["count", "current count", "stock", "qty", "quantity"]) ?? 0),
      low_stock_threshold_count: numOrNull(pick(r, ["low stock threshold", "low_stock_threshold_count", "threshold"])),
      location: String(pick(r, ["location"]) ?? "").trim() || null,
      cost_per_unit: numOrNull(pick(r, ["cost per unit", "cost_per_unit", "cost"])),
      price_per_unit: numOrNull(pick(r, ["price per unit", "price_per_unit", "price"])),
    }))
    .filter((r) => r.sku);
}

export default function PerUnitImportDialog({ onSuccess }) {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const [fileName, setFileName] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const reset = () => { setRows([]); setFileName(""); setConfirmed(false); setResult(null); };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: "array" });
      const json = XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]], { defval: "" });
      const norm = normalizeRows(json);
      setRows(norm);
      setConfirmed(false);
      if (norm.length === 0) toast.error("No rows with a SKU found — check the headers.");
    } catch (err) {
      toast.error("Could not read that file. Use the exported .csv or .xlsx.");
    }
  };

  const distinctSkus = useMemo(() => new Set(rows.map((r) => r.sku.toLowerCase())).size, [rows]);

  const onImport = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/per-unit/import-counts", { rows });
      setResult(data);
      toast.success(`Import complete — updated ${data.updated_count} item(s)`);
      onSuccess?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
      <DialogTrigger asChild>
        <Button variant="outline" className="gap-2 rounded-md" data-testid="per-unit-import-btn">
          <ArrowsClockwise size={16} /> Import (replace)
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><ArrowsClockwise size={20} /> Import &amp; replace per-unit items</DialogTitle>
        </DialogHeader>

        {!result ? (
          <div className="space-y-5 py-2">
            <div className="rounded-md border border-blue-200 bg-blue-50 p-3 text-sm text-blue-800 flex gap-2" data-testid="per-unit-import-explainer">
              <Info size={18} weight="fill" className="shrink-0 mt-0.5" />
              <div>
                <strong>What this does:</strong> each row is matched to an existing per-unit item by its <strong>SKU</strong>. The matched item is <strong>overwritten</strong> with the values in the file — stock count, low-stock threshold, location{" "}
                (and cost/price if you're an admin). Items whose SKU is <strong>not</strong> in the file are left untouched. Any SKU that doesn't exist is reported as an error and skipped. Export first, edit, then re-upload.
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs font-mono tracking-[0.15em] uppercase text-slate-500">Upload exported sheet:</span>
              <label className="inline-flex items-center gap-2 text-xs font-medium h-9 px-3 border border-slate-200 rounded-md cursor-pointer hover:bg-slate-50">
                <UploadSimple size={14} /> Choose .csv / .xlsx
                <input type="file" accept=".csv,.xlsx,.xls" onChange={onFile} className="hidden" data-testid="per-unit-file-input" />
              </label>
              {fileName && <span className="text-xs text-slate-500 truncate max-w-[220px]">{fileName}</span>}
            </div>

            {rows.length > 0 && (
              <>
                <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800 flex gap-2" data-testid="per-unit-import-warning">
                  <Warning size={18} weight="fill" className="shrink-0 mt-0.5" />
                  <div>
                    <strong>{rows.length}</strong> row(s) loaded across <strong>{distinctSkus}</strong> SKU(s). This will
                    {" "}<strong>overwrite the stock and details</strong> of every matching per-unit item. This cannot be undone.
                  </div>
                </div>
                <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                  <Checkbox checked={confirmed} onCheckedChange={setConfirmed} data-testid="per-unit-confirm-check" />
                  I understand these items will be overwritten with the file values.
                </label>
              </>
            )}
          </div>
        ) : (
          <div className="space-y-4 py-2" data-testid="per-unit-import-result">
            <div className="grid grid-cols-2 gap-0 border border-slate-200 rounded-md overflow-hidden">
              <div className="p-4 border-r border-slate-200">
                <CheckCircle weight="fill" size={18} className="text-emerald-600" />
                <div className="text-[10px] font-mono tracking-[0.2em] uppercase text-slate-500 mt-1">Updated</div>
                <div className="font-mono text-3xl font-semibold mt-1 text-emerald-700 tabular-nums">{result.updated_count}</div>
              </div>
              <div className="p-4">
                <Warning weight="fill" size={18} className="text-amber-500" />
                <div className="text-[10px] font-mono tracking-[0.2em] uppercase text-slate-500 mt-1">Errors</div>
                <div className="font-mono text-3xl font-semibold mt-1 text-amber-600 tabular-nums">{result.error_count}</div>
              </div>
            </div>
            {result.errors?.length > 0 && (
              <div className="border border-amber-200 rounded-md max-h-48 overflow-y-auto">
                {result.errors.map((e, i) => (
                  <div key={i} className="px-3 py-2 border-b last:border-0 border-amber-100 text-xs">
                    <span className="font-mono text-slate-500">Row {e.row}:</span> <span className="text-amber-700">{e.error}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          {!result ? (
            <>
              <Button variant="outline" onClick={() => setOpen(false)} className="rounded-md">Cancel</Button>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button disabled={submitting || rows.length === 0 || !confirmed} className="bg-red-600 hover:bg-red-700 rounded-md gap-2" data-testid="per-unit-import-confirm">
                    <ArrowsClockwise size={16} /> {submitting ? "Importing…" : "Replace items"}
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent data-testid="per-unit-are-you-sure">
                  <AlertDialogHeader>
                    <AlertDialogTitle>Are you sure?</AlertDialogTitle>
                    <AlertDialogDescription>
                      This will overwrite the stock and details of <strong>{distinctSkus}</strong> matching per-unit item(s)
                      with the values from <strong>{fileName || "the uploaded file"}</strong>. This action cannot be undone.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel className="rounded-md">Go back</AlertDialogCancel>
                    <AlertDialogAction onClick={onImport} className="bg-red-600 hover:bg-red-700 rounded-md" data-testid="per-unit-are-you-sure-confirm">
                      Yes, replace items
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </>
          ) : (
            <Button onClick={() => setOpen(false)} className="bg-blue-600 hover:bg-blue-700 rounded-md" data-testid="per-unit-import-done">Done</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
